import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store';
import LanguageSelector from './LanguageSelector';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // A dealer's admin (a login with accounts) does not manage the dealer companies.
  const isMainCompany = !user?.accounts || user.accounts.length === 0;

  // Eighteen flat links are a list; five labelled groups are a structure, and it costs
  // nothing but five translation keys. The order follows the working day: what you look
  // at, what you sell, what you sell from, who does the work, who administers it.
  const navGroups = [
    {
      key: 'nav.groupOverview',
      items: [
        { path: '/', labelKey: 'nav.dashboard', icon: DashboardIcon },
        { path: '/visitors', labelKey: 'nav.visitors', icon: VisitorsIcon },
      ],
    },
    {
      key: 'nav.groupSales',
      items: [
        { path: '/orders', labelKey: 'nav.orders', icon: OrdersIcon },
        { path: '/shopping-carts', labelKey: 'nav.shoppingCarts', icon: ShoppingCartIcon },
        { path: '/sales-offers', labelKey: 'nav.salesOffers', icon: SalesOffersIcon },
        { path: '/sales-orders', labelKey: 'nav.salesOrders', icon: SalesOrdersIcon },
      ],
    },
    {
      key: 'nav.groupCatalogue',
      items: [
        { path: '/products', labelKey: 'nav.products', icon: ProductsIcon },
        { path: '/groups', labelKey: 'nav.categories', icon: CategoriesIcon },
        { path: '/prop-models', labelKey: 'nav.propModels', icon: PropModelsIcon },
        { path: '/sandbox', labelKey: 'nav.designer3d', icon: SandboxIcon },
      ],
    },
    {
      key: 'nav.groupWebsite',
      items: [
        { path: '/website/posts', labelKey: 'nav.websitePosts', icon: PostsIcon },
        { path: '/website/pages', labelKey: 'nav.websitePages', icon: PagesIcon },
        { path: '/website/videos', labelKey: 'nav.websiteVideos', icon: VideosIcon },
      ],
    },
    {
      key: 'nav.groupProduction',
      items: [
        { path: '/employees', labelKey: 'nav.employees', icon: EmployeesIcon },
        { path: '/sellers', labelKey: 'nav.sellers', icon: SellersIcon },
        { path: '/resources', labelKey: 'nav.resources', icon: ResourcesIcon },
        { path: '/materials', labelKey: 'nav.materials', icon: MaterialsIcon },
      ],
    },
    {
      key: 'nav.groupAdmin',
      items: [
        ...(isMainCompany ? [{ path: '/resellers', labelKey: 'nav.resellers', icon: ResellersIcon }] : []),
        { path: '/end-users', labelKey: 'nav.websiteUsers', icon: EndUsersIcon },
        { path: '/users', labelKey: 'nav.backofficeUsers', icon: UsersIcon },
        { path: '/settings', labelKey: 'nav.settings', icon: SettingsIcon },
      ],
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-page">
      {/*
        The top bar spans the FULL width, including over the logo. It used to start where
        the rail ended, which made it read as an offcut, and the logo sat on the dark rail
        where its own greens went dark-on-dark and the mark lost its edges. On white it
        reads as drawn, and identity is separated from navigation instead of stacked on it.
      */}
      {/*
        z-50, one step ABOVE the rail's z-40. Both are sticky, so both create a stacking
        context; when they shared z-40 the rail won purely by being later in the DOM, and
        it painted over the language dropdown - whose own z-50 cannot escape this header's
        context. Order is deliberate: backdrop 30 < rail 40 < top bar 50 < modals 50-and-later.
      */}
      <header className="sticky top-0 z-50 h-[76px] flex items-center bg-white border-b border-hair">
        <Link to="/" className="w-64 shrink-0 hidden lg:flex items-center gap-3 px-6">
          <img src="/happyeco-logo.png" alt="" className="h-9 w-9 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-[15px] font-semibold text-ink-900 leading-tight truncate">
              {t('common.appName')}
            </h1>
            <p className="eyebrow mt-0.5">Backoffice</p>
          </div>
        </Link>

        <div className="flex-1 flex items-center gap-3 px-4 lg:px-7 min-w-0">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-1 text-ink-400 hover:text-ink-600"
          >
            <MenuIcon className="w-6 h-6" />
          </button>

          <img src="/happyeco-logo.png" alt="" className="h-8 w-8 lg:hidden" />

          {/* Language Selector - Left */}
          <LanguageSelector />

          <div className="flex-1" />

          {/* Who is signed in, then the two account actions as matched square tiles. */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block leading-tight">
              <p className="text-[13px] font-semibold text-ink-900 truncate max-w-[14rem]">
                {user?.userName || user?.email}
              </p>
              <p className="text-[11px] text-ink-400 mt-0.5">{t('nav.administrator')}</p>
            </div>

            <Link
              to="/settings"
              title={t('nav.settings')}
              className="w-[38px] h-[38px] rounded-[11px] border border-hair text-ink-400
                hover:text-ink-600 hover:bg-page flex items-center justify-center transition-colors"
            >
              <GearIcon className="w-[17px] h-[17px]" />
            </Link>

            <button
              onClick={handleLogout}
              title={t('auth.signOut')}
              className="w-[38px] h-[38px] rounded-[11px] border border-hair text-ink-400
                hover:text-red-600 hover:border-red-200 hover:bg-red-50
                flex items-center justify-center transition-colors"
            >
              <LogoutIcon className="w-[17px] h-[17px]" />
            </button>
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------ rail + page */}
      <div className="flex">
        {sidebarOpen && (
          <div
            className="fixed inset-0 top-[76px] z-30 bg-graphite-950/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/*
          Each icon sits in its own tile. A column of bare glyphs is hard to scan; a column
          of objects is not, and the active item can then be stated properly - a solid lime
          tile with a dark glyph, which is the logo's own colour pairing - instead of as a
          faint tint. Lime never carries text: white on it is 1.8:1, the dark glyph is 9.9:1.
        */}
        <aside
          className={`w-64 shrink-0 z-40
            bg-gradient-to-b from-graphite-800 to-graphite-950
            fixed inset-y-0 top-[76px] left-0 overflow-y-auto
            lg:sticky lg:top-[76px] lg:h-[calc(100vh-76px)] lg:translate-x-0
            transform transition-transform duration-200 ease-in-out ${
              sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
          <nav className="px-3.5 py-4">
            {navGroups.map((group) => (
              <div key={group.key}>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.09em]
                  text-white/35 px-2.5 pt-3.5 pb-2 first:pt-1">
                  {t(group.key)}
                </div>
                {group.items.map(({ path, labelKey, icon: Icon }) => {
                  const on = isActive(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setSidebarOpen(false)}
                      // Semantic, and what any click-through test should key off. Asserting
                      // on a styling class makes a restyle look like broken navigation.
                      aria-current={on ? 'page' : undefined}
                      className={`flex items-center gap-3 px-2.5 py-1.5 mb-0.5 rounded-[11px]
                        text-[13.5px] transition-colors ${
                          on
                            ? 'bg-white/10 text-white font-semibold'
                            : 'text-white/75 font-medium hover:bg-white/[0.06] hover:text-white'
                        }`}
                    >
                      <span
                        className={`w-[34px] h-[34px] rounded-[10px] shrink-0
                          flex items-center justify-center transition-colors ${
                            on
                              ? 'bg-happy-lime-500 text-happy-green-950 shadow-[0_2px_10px_-2px_rgba(166,206,57,0.55)]'
                              : 'bg-white/[0.06] text-white/55'
                          }`}
                      >
                        <Icon className="w-[17px] h-[17px]" />
                      </span>
                      <span className="truncate">{t(labelKey)}</span>
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        {/* Page content */}
        <main className="flex-1 min-w-0 p-5 lg:p-7">
          {children}
        </main>
      </div>
    </div>
  );
}

// Icons
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function PostsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  );
}

function PagesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function VideosIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function VisitorsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function OrdersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}

function ProductsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function CategoriesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function ResellersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function EndUsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function EmployeesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
    </svg>
  );
}

function SellersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function ResourcesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
    </svg>
  );
}

function MaterialsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function PropModelsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
    </svg>
  );
}

function SandboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function ShoppingCartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

// Sales Offers icon (quotes/proposals)
function SalesOffersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

// Sales Orders icon (confirmed orders with production workflow)
function SalesOrdersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}
